import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Divider, Icon, IconButton, Input, Select, RadioList, TOption } from '@treezor/design-system';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useInputs } from '@/hooks/useInputs';
import { setBulkCardCreationStep } from '@/store/actions/bulkCardCreations.actions';
import { WalletTypeForBulkCardCreation } from '@/features/wallets/store/models/wallet.model';
import { countriesCodes } from '@/utils/constants';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';

interface IWalletAndUserFormInputs {
  walletIdAttach?: number;
  walletTypeId?: number;
  userIdOwner?: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  createWalletForEach?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  createUserForEach?: boolean;
  cardDeliveryAddress1: string;
  cardDeliveryAddress2?: string;
  cardDeliveryAddress3?: string;
  cardDeliveryPostCode: string;
  cardDeliveryCity: string;
  cardDeliveryState: string;
  cardDeliveryCountry: string;
  distributionCountry?: string;
}

export const BulkWalletAndUser = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const config = useDashboardConfig();

  const activeStep = useAppSelector((state) => state.bulkCardCreation.currentStep);
  const tmpBBC = useAppSelector((state) => state.bulkCardCreation.bulkCardCreationDraft);
  const isCreatingVirtualCards = tmpBBC?.medium === 2;

  const [fields, setfields] = useState(0);

  const { countryCodeOptions } = useFieldOptions();

  const distributionCountry = config?.settings?.users?.distributionCountry;
  const hasDistributionCountryInConfig = !!distributionCountry?.length;

  const cardDeliveryAddress = config?.settings.cards.defaultDeliveryAddress;

  const defaultValues = {
    walletIdAttach: tmpBBC?.walletIdAttach,
    walletTypeId: tmpBBC?.walletTypeId,
    userIdOwner: tmpBBC?.userIdOwner,
    createWalletForEach: !!tmpBBC?.createWalletForEach,
    createUserForEach: !!tmpBBC?.createUserForEach,
    cardDeliveryAddress1: tmpBBC?.cardDeliveryAddress1 || cardDeliveryAddress?.address1,
    cardDeliveryAddress2: tmpBBC?.cardDeliveryAddress2 || cardDeliveryAddress?.address2,
    cardDeliveryAddress3: tmpBBC?.cardDeliveryAddress3 || cardDeliveryAddress?.address3,
    cardDeliveryPostCode: tmpBBC?.cardDeliveryPostCode || cardDeliveryAddress?.postCode,
    cardDeliveryCity: tmpBBC?.cardDeliveryCity || cardDeliveryAddress?.city,
    cardDeliveryState: tmpBBC?.cardDeliveryState || cardDeliveryAddress?.state,
    cardDeliveryCountry: tmpBBC?.cardDeliveryCountry || cardDeliveryAddress?.country,
    distributionCountry: tmpBBC?.distributionCountry,
  };
  const formMethods = useForm<IWalletAndUserFormInputs>({ defaultValues });

  const {
    watch,
    clearErrors,
    setError,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = formMethods;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const createWalletWatch = watch('createWalletForEach');
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const createUserWatch = watch('createUserForEach');

  const methods = useInputs(
    [
      'walletIdAttach',
      {
        name: 'walletTypeId',
        type: 'select',
      },
      'userIdOwner',
      {
        name: 'cardDeliveryAddress1',
        type: 'text',
        options: {
          required: {
            value: createUserWatch,
            message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryAddress'),
          },
        },
      },
      'cardDeliveryAddress2',
      'cardDeliveryAddress3',
      {
        name: 'cardDeliveryPostCode',
        type: 'text',
        options: {
          required: {
            value: createUserWatch,
            message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryPostcode'),
          },
        },
      },
      {
        name: 'cardDeliveryCity',
        type: 'text',
        options: {
          required: {
            value: createUserWatch,
            message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryCity'),
          },
        },
      },
      'cardDeliveryState',
      {
        name: 'cardDeliveryCountry',
        type: 'select',
        options: {
          required: {
            value: createUserWatch,
            message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryCountry'),
          },
        },
      },
      {
        name: 'distributionCountry',
        type: 'select',
      },
    ],
    formMethods,
  );

  const walletIdSelectOptions: TOption[] = Object.keys(WalletTypeForBulkCardCreation).map((key) => ({
    label: `${key} ${WalletTypeForBulkCardCreation[key]}`,
    value: key,
  }));

  const countriesData = useMemo(
    () =>
      countriesCodes
        .map((key) => ({
          label: t(`shared.countries.${key}`),
          value: key,
        }))
        .sort((countryA, countryB) => (countryA.label <= countryB.label ? -1 : 1)),
    [t],
  );

  const onSubmit = (data: IWalletAndUserFormInputs) => {
    clearErrors();
    if (!data) return;
    const tmpErrors: { name: keyof IWalletAndUserFormInputs; message: string }[] = [];

    if (!createWalletWatch && !data.walletIdAttach)
      tmpErrors.push({
        name: 'walletIdAttach',
        message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryWalletId'),
      });

    if (createWalletWatch && !data.walletTypeId)
      tmpErrors.push({
        name: 'walletTypeId',
        message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryWalletType'),
      });

    if (!createUserWatch && !data.userIdOwner)
      tmpErrors.push({
        name: 'userIdOwner',
        message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryUserId'),
      });

    if (createUserWatch && !data.distributionCountry && hasDistributionCountryInConfig) {
      tmpErrors.push({
        name: 'distributionCountry',
        message: t('pages.administration.bulkCards.walletAndUser.forms.rules.mandatoryDistributionCountry'),
      });
    }

    if (tmpErrors.length) tmpErrors.forEach((error) => setError(error.name, { message: error.message }));
    else {
      dispatch(
        setBulkCardCreationStep(activeStep + 1, {
          ...data,
          walletIdAttach: createWalletWatch ? undefined : Number(data.walletIdAttach),
          walletTypeId: createWalletWatch ? Number(data.walletTypeId) : undefined,
          userIdOwner: createUserWatch ? undefined : Number(data.userIdOwner),
          cardDeliveryAddress1: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryAddress1,
          cardDeliveryAddress2: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryAddress2,
          cardDeliveryAddress3: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryAddress3,
          cardDeliveryCity: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryCity,
          cardDeliveryCountry: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryCountry,
          cardDeliveryPostCode: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryPostCode,
          cardDeliveryState: isCreatingVirtualCards || !createUserWatch ? undefined : data.cardDeliveryState,
          distributionCountry:
            !createUserWatch && hasDistributionCountryInConfig ? undefined : data.distributionCountry,
        }),
      );
    }
  };

  return (
    <>
      <h5 className="typo-h5-bold text-secondary">{t('pages.administration.bulkCards.walletAndUser.title')}</h5>
      <form className="flex flex-col gap-7 mt-8 w-[360px]" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-5">
          <RadioList
            name="createWalletForEach"
            options={[
              {
                value: 'false',
                label: t('pages.administration.bulkCards.walletAndUser.forms.createWalletForEach.options.no'),
              },
              {
                value: 'true',
                label: t('pages.administration.bulkCards.walletAndUser.forms.createWalletForEach.options.yes'),
              },
            ]}
            onChange={(value) => setValue('createWalletForEach', value === 'true' ? true : false)}
            value={watch('createWalletForEach') ? 'true' : 'false'}
            className="!w-[360px] justify-between"
            direction="row"
          />

          <div>
            {createWalletWatch ? (
              <Select
                options={walletIdSelectOptions}
                placeholder={t('pages.administration.bulkCards.walletAndUser.forms.walletType')}
                defaultValue={getValues('walletTypeId')?.toString()}
                onChange={methods.handleWalletTypeIdChange}
                error={errors.walletTypeId?.message}
              />
            ) : (
              <Input
                type="text"
                name="walletIdAttach"
                placeholder={t('pages.administration.bulkCards.walletAndUser.forms.walletId')}
                defaultValue={getValues('walletIdAttach')}
                onChange={methods.handleWalletIdAttachChange}
                error={errors.walletIdAttach?.message}
              />
            )}
          </div>
        </div>

        <Divider />

        <div className="flex flex-col gap-5">
          <RadioList
            name="createUserForEach"
            options={[
              {
                value: 'false',
                label: t('pages.administration.bulkCards.walletAndUser.forms.createUserForEach.options.no'),
              },
              {
                value: 'true',
                label: t('pages.administration.bulkCards.walletAndUser.forms.createUserForEach.options.yes'),
              },
            ]}
            onChange={(value) => setValue('createUserForEach', value === 'true' ? true : false)}
            value={watch('createUserForEach') ? 'true' : 'false'}
            className="!w-[360px] justify-between"
            direction="row"
          />

          {!createUserWatch && (
            <Input
              type="text"
              name="userIdOwner"
              placeholder={t('pages.administration.bulkCards.walletAndUser.forms.userId')}
              defaultValue={getValues('userIdOwner')}
              onChange={methods.handleUserIdOwnerChange}
              error={errors.userIdOwner?.message}
            />
          )}

          {createUserWatch && (
            <div className="flex flex-col gap-6 w-full mt-6">
              {hasDistributionCountryInConfig && (
                <Select
                  required
                  withSearch
                  label={t('pages.currentProfiles.profile.informations.distributionCountry')}
                  options={countryCodeOptions.filter(({ value }) => distributionCountry.includes(value))}
                  defaultValue={getValues('distributionCountry')}
                  noOptionsComponent={<CreateUserEmptyComponent />}
                  onChange={methods.handleDistributionCountryChange}
                  error={errors?.distributionCountry?.message}
                />
              )}
              {!isCreatingVirtualCards && (
                <>
                  <Input
                    type="text"
                    name="cardDeliveryAddress1"
                    label={t('pages.administration.bulkCards.walletAndUser.forms.address1')}
                    defaultValue={getValues('cardDeliveryAddress1')}
                    onChange={methods.handleCardDeliveryAddress1Change}
                    error={errors.cardDeliveryAddress1?.message}
                  />
                  {fields > 0 && (
                    <div className="flex items-end gap-2">
                      <Input
                        type="text"
                        name="cardDeliveryAddress2"
                        label={t('pages.administration.bulkCards.walletAndUser.forms.address2')}
                        defaultValue={getValues('cardDeliveryAddress2')}
                        onChange={methods.handleCardDeliveryAddress2Change}
                      />
                      <IconButton size="l" icon="remove" onClick={() => setfields(fields - 1)} />
                    </div>
                  )}
                  {fields > 1 && (
                    <div className="flex items-end gap-2">
                      <Input
                        type="text"
                        name="cardDeliveryAddress3"
                        label={t('pages.administration.bulkCards.walletAndUser.forms.address3')}
                        defaultValue={getValues('cardDeliveryAddress3')}
                        onChange={methods.handleCardDeliveryAddress3Change}
                      />
                      <IconButton size="l" icon="remove" onClick={() => setfields(fields - 1)} />
                    </div>
                  )}
                  <Button disabled={fields > 2} type="button" onClick={() => setfields(fields + 1)} variant="tertiary">
                    <Icon name="add" isFill={false} />
                    {t('pages.administration.bulkCards.walletAndUser.forms.addAddress')}
                  </Button>
                  <Input
                    type="text"
                    name="cardDeliveryPostCode"
                    label={t('pages.administration.bulkCards.walletAndUser.forms.postcode')}
                    defaultValue={getValues('cardDeliveryPostCode')}
                    onChange={methods.handleCardDeliveryPostCodeChange}
                    error={errors.cardDeliveryPostCode?.message}
                  />
                  <Input
                    type="text"
                    name="cardDeliveryCity"
                    label={t('pages.administration.bulkCards.walletAndUser.forms.city')}
                    defaultValue={getValues('cardDeliveryCity')}
                    onChange={methods.handleCardDeliveryCityChange}
                    error={errors.cardDeliveryCity?.message}
                  />
                  <Input
                    type="text"
                    name="cardDeliveryState"
                    label={t('pages.administration.bulkCards.walletAndUser.forms.state')}
                    defaultValue={getValues('cardDeliveryState')}
                    onChange={methods.handleCardDeliveryStateChange}
                  />
                  <Select
                    options={countriesData}
                    label={t('pages.administration.bulkCards.walletAndUser.forms.country')}
                    defaultValue={getValues('cardDeliveryCountry')}
                    onChange={methods.handleCardDeliveryCountryChange}
                  />
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-center">
          <Button id="bulk-wallet-and-user-next-btn" type="submit">
            {t('pages.administration.bulkCards.next')}
          </Button>
        </div>
      </form>
    </>
  );
};
